/**
 * Functions to compute SVG path params for rendering biointelligent links.
 */

import {Strand} from 'constants/MolbioConstants';
import {
  BiointelligentLinkType,
  LINK_TYPE_TO_ICON_ID,
  LINK_TYPE_TO_LINKED_ITEM_NAME,
} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {
  BiointelligentLinkAnnotation,
  DNA_PART_ARROW_TIP_HEIGHT,
  DNA_PART_ARROW_TIP_WIDTH,
  DNA_PART_TICK_HEIGHT,
  DNA_PART_TICK_SPACING,
  LINK_TO_PROTEIN_ANNOTATION_PADDING,
  LINK_TO_PROTEIN_ARROW_CIRCLE_RADIUS,
  LINK_TO_PROTEIN_ARROW_TIP_HEIGHT,
  LINK_TO_PROTEIN_DOT_SPACING,
  LINKED_ENTITY_CHIP_HORIZONTAL_PADDING,
  LINKED_ENTITY_CHIP_ICON_SIZE,
  TRANSCRIPTION_ARROW_TIP_WIDTH,
  SINGLE_ANNOTATION_HEIGHT,
  TRANSCRIPTION_TICK_SPACING,
  TRANSCRIPTION_ARROW_TIP_HEIGHT,
  TRANSCRIPTION_TICK_HEIGHT,
} from 'molbio/maps/BiointelligentLinksConstants';
import {formatAnnotationDisplayName} from 'molbio/maps/linear/AnnotationsHelpers';
import Type from 'molbio/maps/Type';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Parameters for drawing an arrow line for the visualization of a Biointelligent Link. These are intended to
 * be passed through as props into an SVG line element.
 */
interface LineParams {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

/**
 * Parameters for drawing a dot for the visualization of a Biointelligent Link to a protein. These are
 * intended to be passed through as props into an SVG circle element.
 */
interface ProteinCircleParams {
  cx: number;
  cy: number;
}

/**
 * Params for rendering the display name of the component polymer in the visualization a Biointelligent
 * Link.
 */
interface DisplayNameParams {
  /*
   * The text to render. This may be ellipsized from the actual name of the polymer. If this value is empty
   * string, there is not enough space to render the display name and it should be omitted.
   */
  displayName: string;
  iconId: BenchlingIconName;
  /*
   * The width in pixels that the display name 'chip' will take up.
   */
  width: number;
}

/**
 * Params for drawing the inner visualization of a biointelligent link to a protein.
 */
interface LinkToProteinPathParams {
  /*
   * The path of the tip of the arrow in the visualization of a Biointelligent Link to a protein. This is
   * intended to be passed through as a prop to an SVG path element.
   */
  arrowPointD: string | null;
  circleParams: Array<ProteinCircleParams>;
  displayNameParams: DisplayNameParams;
  lineParams: LineParams;
}

interface LinkToSequenceLineParams extends LineParams {
  arrowPointD: string | null;
}

/**
 * Params for drawing the inner visualization of a biointelligent link to a DNA sequence.
 */
export interface LinkToDNASequencePathParams {
  topLineParams: LinkToSequenceLineParams;
  bottomLineParams: LinkToSequenceLineParams;
  dnaTickParams: Array<LineParams>;
  displayNameParams: DisplayNameParams;
}

/* Util functions for all types of biointelligent links */

function isReverseLink(direction: Strand): boolean {
  return direction === -1;
}

function scaleAnnotationLineY(height: number, position: 'top' | 'bottom' | 'center'): number {
  switch (position) {
    case 'top':
      return height / 4;
    case 'bottom':
      return (height / 4) * 3;
    case 'center':
      return height / 2;
    default:
      throw new Error(`Invalid position: ${position}`);
  }
}

function getArrowPath(
  annotationWidth: number,
  annotationHeight: number,
  position: 'top' | 'bottom' | 'center',
  direction: Strand,
  arrowTipWidth: number,
  arrowTipHeight: number
): string {
  const heightScaleFactor = scaleAnnotationLineY(annotationHeight, position);
  const scaleXPosition = (x: number): number => (isReverseLink(direction) ? x : annotationWidth - x);

  const arrowPointTop = `M ${scaleXPosition(arrowTipWidth)} ${heightScaleFactor + arrowTipHeight / 2} `;
  const arrowPointMiddle = `L ${scaleXPosition(arrowTipWidth / 2)} ${heightScaleFactor} `;
  const arrowPointBottom = `L ${scaleXPosition(arrowTipWidth)} ${heightScaleFactor - arrowTipHeight / 2}`;
  return arrowPointTop + arrowPointMiddle + arrowPointBottom;
}

function shouldShowArrows(
  sliceStart: number | null,
  sliceEnd: number,
  linkStart: number | null,
  linkEnd: number,
  linkDirection: Strand
): boolean {
  const wrapsRight = linkEnd > sliceEnd;
  const wrapsLeft = sliceStart != null && linkStart != null && sliceStart > linkStart;
  return !((wrapsRight && !isReverseLink(linkDirection)) || (wrapsLeft && isReverseLink(linkDirection)));
}

export function getDisplayNameParams(
  linkType: BiointelligentLinkType,
  linkedItemName: string | null,
  fontType: Type,
  width: number,
  linkAnnotationPadding: number,
  lineMarkingSpacing: number,
  shouldShowArrows: boolean
): DisplayNameParams {
  let displayName = linkedItemName ?? `Inaccessible ${LINK_TYPE_TO_LINKED_ITEM_NAME[linkType]}`;
  const displayNameWidth = fontType.width(displayName.length);
  const constantChipWidth =
    2 * linkAnnotationPadding +
    // 3x instead of 2x is because we reuse this padding to separate the icon and the text
    3 * LINKED_ENTITY_CHIP_HORIZONTAL_PADDING +
    LINKED_ENTITY_CHIP_ICON_SIZE;
  let availableWidth: number = width - 3 * lineMarkingSpacing - constantChipWidth;
  // If we're not wrapping, we need to make a bit of extra space for the arrow.
  if (shouldShowArrows) {
    availableWidth -= lineMarkingSpacing;
  }
  const spaceInside = displayNameWidth < availableWidth;
  if (!spaceInside) {
    const numAvailableChars = availableWidth / fontType.charWidth();
    displayName = formatAnnotationDisplayName(displayName, numAvailableChars);
  }
  const chipWidth =
    // The expected width of the text of the entity name...
    fontType.width(displayName.length) +
    // ...plus the left & right padding, plus the spacing between the icon and the text...
    3 * LINKED_ENTITY_CHIP_HORIZONTAL_PADDING +
    // ...plus the width of the icon.
    LINKED_ENTITY_CHIP_ICON_SIZE;
  return {displayName, iconId: LINK_TYPE_TO_ICON_ID[linkType], width: chipWidth};
}

/* Functions to compute protein part link drawing params */

function getProteinDotsParams(
  sliceEnd: number,
  end: number,
  width: number,
  height: number
): Array<ProteinCircleParams> {
  const wrapsRight = end > sliceEnd;
  // Don't overlap the arrow with circles.
  const circleCxs: Array<number> = [];
  // Don't allow circles closer to the left edge than the 'padding' of the rect.
  const firstCircleCx = LINK_TO_PROTEIN_ARROW_CIRCLE_RADIUS + LINK_TO_PROTEIN_ANNOTATION_PADDING;
  // Start from one circle's worth away from the edge of the arrow and work left. In most cases, the last few
  // circles will be overlapped by the chip.
  const rightOffset = wrapsRight ? LINK_TO_PROTEIN_DOT_SPACING : LINK_TO_PROTEIN_DOT_SPACING * 2;
  let dotCx = width - rightOffset;
  while (dotCx >= firstCircleCx) {
    circleCxs.push(dotCx);
    dotCx -= LINK_TO_PROTEIN_DOT_SPACING;
  }
  const circleParams: Array<ProteinCircleParams> = [];
  for (const cx of circleCxs) {
    const circleParam = {
      cx,
      cy: height / 2,
    };
    circleParams.push(circleParam);
  }
  return circleParams;
}

/**
 * Get all the parameters necessary for drawing a link to a protein in the linear / sequence map.
 * @param end The index in base pairs of the end of the link
 * @param height The height in pixels of the link visualization
 * @param proteinName The name of the protein being linked to
 * @param sliceEnd The index in base pairs of the end of the sequence map slice.
 * @param type A Type object, the Type for rendering the protein name
 * @param width The width in pixels of the link visualization
 */
export function getLinkToProteinPathParams({
  end,
  height,
  proteinName,
  sliceEnd,
  type,
  width,
}: {
  end: number;
  height: number;
  proteinName: string | null;
  sliceEnd: number;
  type: Type;
  width: number;
}): LinkToProteinPathParams {
  const shouldShowProteinArrow = shouldShowArrows(null, sliceEnd, null, end, 0);

  const arrowPointD = shouldShowProteinArrow
    ? getArrowPath(width, height, 'center', 0, LINK_TO_PROTEIN_DOT_SPACING, LINK_TO_PROTEIN_ARROW_TIP_HEIGHT)
    : null;

  const circleParams = getProteinDotsParams(sliceEnd, end, width, height);

  const displayNameParams = getDisplayNameParams(
    BiointelligentLinkType.AMINO_ACID_PART,
    proteinName,
    type,
    width,
    LINK_TO_PROTEIN_ANNOTATION_PADDING,
    LINK_TO_PROTEIN_DOT_SPACING,
    shouldShowProteinArrow
  );

  // If there is a visible arrow, go all the way to the arrow; otherwise, go all the way to the edge of the
  // annotation
  const lineParams: LineParams = {
    x1: LINK_TO_PROTEIN_ANNOTATION_PADDING,
    x2: shouldShowProteinArrow
      ? width - LINK_TO_PROTEIN_DOT_SPACING
      : width - LINK_TO_PROTEIN_ANNOTATION_PADDING,
    y1: height / 2,
    y2: height / 2,
  };

  return {
    arrowPointD,
    circleParams,
    displayNameParams,
    lineParams,
  };
}

/* Functions to compute DNA sequence part link drawing params */

function getDNATickLineParams(width: number, height: number, position: 'top' | 'bottom'): Array<LineParams> {
  const DNATickParams: Array<LineParams> = [];

  // Start the first tick a little bit behind the arrow, half a full tick spacing.
  const offset = DNA_PART_ARROW_TIP_WIDTH + DNA_PART_TICK_SPACING / 2;

  const firstTickX = offset;
  let tickX = width - offset;
  while (tickX >= firstTickX) {
    // To make the tick appear to extend "downward" from the top line and "upward" from the bottom line,
    // we need to adjust the starting Y position accordingly.
    const verticalAdjustmentFactor = position === 'top' ? 0 : -DNA_PART_TICK_HEIGHT;
    const startingY = scaleAnnotationLineY(height, position) + verticalAdjustmentFactor;
    DNATickParams.push({
      x1: tickX,
      x2: tickX,
      y1: startingY,
      y2: startingY + DNA_PART_TICK_HEIGHT,
    });
    tickX -= DNA_PART_TICK_SPACING;
  }

  return DNATickParams;
}

/**
 * Get all the parameters necessary for drawing a link to a DNA sequence in the linear / sequence map.
 * @param link The link to draw
 * @param sliceStart The index in base pairs of the start of the sequence map slice.
 * @param sliceEnd The index in base pairs of the end of the sequence map slice.
 * @param type A Type object, the Type for rendering the linked sequence name
 * @param width The width in pixels of the link annotation visualization
 * @param height The height in pixels of the link annotation visualization
 */
export function getLinkToSequencePathParams(
  link: BiointelligentLinkAnnotation,
  sliceStart: number,
  sliceEnd: number,
  type: Type,
  width: number,
  height: number
): LinkToDNASequencePathParams {
  const linkDirection = link.direction;
  const shouldShowBothArrows = shouldShowArrows(sliceStart, sliceEnd, link.start, link.end, linkDirection);
  const topArrowPointD: string | null = shouldShowBothArrows
    ? getArrowPath(width, height, 'top', linkDirection, DNA_PART_ARROW_TIP_WIDTH, DNA_PART_ARROW_TIP_HEIGHT)
    : null;
  const bottomArrowPointD: string | null = shouldShowBothArrows
    ? getArrowPath(
        width,
        height,
        'bottom',
        linkDirection,
        DNA_PART_ARROW_TIP_WIDTH,
        DNA_PART_ARROW_TIP_HEIGHT
      )
    : null;
  const startingX = isReverseLink(linkDirection)
    ? DNA_PART_ARROW_TIP_WIDTH
    : LINKED_ENTITY_CHIP_HORIZONTAL_PADDING;
  const lineWidth = isReverseLink(linkDirection)
    ? width - DNA_PART_ARROW_TIP_WIDTH - LINKED_ENTITY_CHIP_HORIZONTAL_PADDING * 2
    : width - DNA_PART_ARROW_TIP_WIDTH;
  return {
    topLineParams: {
      x1: startingX,
      x2: startingX + lineWidth,
      y1: scaleAnnotationLineY(SINGLE_ANNOTATION_HEIGHT, 'top'),
      y2: scaleAnnotationLineY(SINGLE_ANNOTATION_HEIGHT, 'top'),
      arrowPointD: topArrowPointD,
    },
    bottomLineParams: {
      x1: startingX,
      x2: startingX + lineWidth,
      y1: scaleAnnotationLineY(SINGLE_ANNOTATION_HEIGHT, 'bottom'),
      y2: scaleAnnotationLineY(SINGLE_ANNOTATION_HEIGHT, 'bottom'),
      arrowPointD: bottomArrowPointD,
    },
    dnaTickParams: [
      ...getDNATickLineParams(width, height, 'top'),
      ...getDNATickLineParams(width, height, 'bottom'),
    ],
    displayNameParams: getDisplayNameParams(
      link.linkType,
      link.name,
      type,
      width,
      LINKED_ENTITY_CHIP_HORIZONTAL_PADDING,
      DNA_PART_TICK_SPACING,
      shouldShowBothArrows
    ),
  };
}

export interface LinkToRnaSequencePathParams {
  lineParams: LineParams;
  arrowPointD: string | null;
  rnaTickParams: Array<LineParams>;
  displayNameParams: DisplayNameParams;
}

function getRnaTickLineParams(width: number, height: number): Array<LineParams> {
  const rnaTickParams: Array<LineParams> = [];

  // Start the first tick a little bit behind the arrow, half a full tick spacing.
  const offset = TRANSCRIPTION_ARROW_TIP_WIDTH + TRANSCRIPTION_TICK_SPACING / 2;

  const firstTickX = offset;
  let tickX = width - offset;
  while (tickX >= firstTickX) {
    const startingY = scaleAnnotationLineY(height, 'center');
    rnaTickParams.push({
      x1: tickX,
      x2: tickX,
      y1: startingY,
      y2: startingY + TRANSCRIPTION_TICK_HEIGHT,
    });
    tickX -= TRANSCRIPTION_TICK_SPACING;
  }

  return rnaTickParams;
}

export function getLinkToRnaSequencePathParams(
  link: BiointelligentLinkAnnotation,
  sliceStart: number,
  sliceEnd: number,
  type: Type,
  width: number,
  height: number
): LinkToRnaSequencePathParams {
  const linkDirection = link.direction;
  const shouldShowArrow = shouldShowArrows(sliceStart, sliceEnd, link.start, link.end, linkDirection);
  const arrowPointD: string | null = shouldShowArrow
    ? getArrowPath(
        width,
        height,
        'center',
        linkDirection,
        TRANSCRIPTION_ARROW_TIP_WIDTH,
        TRANSCRIPTION_ARROW_TIP_HEIGHT
      )
    : null;
  const lineParams: LineParams = {
    x1: TRANSCRIPTION_ARROW_TIP_WIDTH,
    x2: width - TRANSCRIPTION_ARROW_TIP_WIDTH,
    y1: scaleAnnotationLineY(SINGLE_ANNOTATION_HEIGHT, 'center'),
    y2: scaleAnnotationLineY(SINGLE_ANNOTATION_HEIGHT, 'center'),
  };
  const displayNameParams = getDisplayNameParams(
    link.linkType,
    link.name,
    type,
    width,
    LINKED_ENTITY_CHIP_HORIZONTAL_PADDING,
    TRANSCRIPTION_TICK_SPACING,
    shouldShowArrow
  );
  return {
    lineParams,
    arrowPointD,
    displayNameParams,
    rnaTickParams: getRnaTickLineParams(width, height),
  };
}
